import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Send, EyeOff, Search, ChevronDown, ChevronRight, X, HelpCircle, FileText, BookOpen, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import useKnowledgeBaseStore from '@/stores/AdminStore/KnowledgeBaseStore';
import { LoadingSpinner, MarkdownEditor } from '@/Components/common';
import { ChangelogItem } from '@/services/PagesServices/KnowledgeBaseService';
import './KnowledgeBaseManagement.css';

const ITEM_TYPES = [
  { value: 'feature', label: 'Feature', color: '#10b981' },
  { value: 'improvement', label: 'Improvement', color: '#3b82f6' },
  { value: 'bugfix', label: 'Bug Fix', color: '#f59e0b' },
  { value: 'breaking', label: 'Breaking', color: '#ef4444' },
];

// Audience targeting: role ids 1=Learner, 2=Trainer, 3=Admin, 4=Super Admin.
// `audience_roles` is '' (Public — anyone, incl. logged-out) or a CSV of ids.
const ROLE_OPTIONS = [
  { id: '1', label: 'Learner' },
  { id: '2', label: 'Trainer' },
  { id: '3', label: 'Admin' },
  { id: '4', label: 'Super Admin' },
];

const audienceLabels = (roles?: string | null): string =>
  !roles ? '' : roles.split(',').map((r) => ROLE_OPTIONS.find((o) => o.id === r.trim())?.label).filter(Boolean).join(', ');

// Public vs role-restricted selector, shared by all three KB forms.
const AudienceSelector: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const isRestricted = !!value;
  const selected = new Set(value.split(',').map((s) => s.trim()).filter(Boolean));
  const toggle = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(ROLE_OPTIONS.filter((o) => next.has(o.id)).map((o) => o.id).join(','));
  };
  return (
    <div className="rn-form-field">
      <label>Visibility</label>
      <div style={{ display: 'flex', gap: 16, marginBottom: isRestricted ? 8 : 0 }}>
        <label className="rn-checkbox-label" style={{ margin: 0 }}>
          <input type="radio" checked={!isRestricted} onChange={() => onChange('')} /> Public (anyone)
        </label>
        <label className="rn-checkbox-label" style={{ margin: 0 }}>
          <input type="radio" checked={isRestricted} onChange={() => onChange('1')} /> Restricted to roles
        </label>
      </div>
      {isRestricted && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {ROLE_OPTIONS.map((o) => (
            <label key={o.id} className="rn-checkbox-label" style={{ margin: 0 }}>
              <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} /> {o.label}
            </label>
          ))}
        </div>
      )}
      {isRestricted && selected.size === 0 && (
        <small style={{ color: '#ef4444' }}>Select at least one role, or switch to Public.</small>
      )}
    </div>
  );
};

const KnowledgeBaseManagement: React.FC = () => {
  const {
    changelog, faqs, articles, pagination, articlesPagination,
    loading, loadingFaqs, loadingArticles, saving,
    fetchChangelog, fetchChangelogEntry, createEntry, updateEntry, publishEntry, unpublishEntry, deleteEntry,
    fetchFaqs, createFaqItem, updateFaqItem, deleteFaqItem,
    fetchArticles, fetchArticle, createArticleItem, updateArticleItem, deleteArticleItem,
  } = useKnowledgeBaseStore();

  const [activeTab, setActiveTab] = useState<'articles' | 'changelog' | 'faqs'>('articles');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Changelog form
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntryUuid, setEditingEntryUuid] = useState<string | null>(null);
  const [entryForm, setEntryForm] = useState({ version: '', title: '', description: '', published_date: '', status: 'draft' as string, audience_roles: '' });
  const [entryItems, setEntryItems] = useState<Partial<ChangelogItem>[]>([]);

  // FAQ form
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaqUuid, setEditingFaqUuid] = useState<string | null>(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'General', sort_order: 0, is_active: true, audience_roles: '' });

  // Article form
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticleUuid, setEditingArticleUuid] = useState<string | null>(null);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', excerpt: '', category: 'General', tags: '', status: 'draft' as string, audience_roles: '' });

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'changelog' | 'faq' | 'article'; uuid: string } | null>(null);

  // FAQ categories collapsed
  const [collapsedCats, setCollapsedCats] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (activeTab === 'changelog') fetchChangelog(1, search, statusFilter);
    else if (activeTab === 'faqs') fetchFaqs(search);
    else if (activeTab === 'articles') fetchArticles(1, search, '', statusFilter);
  }, [activeTab, fetchChangelog, fetchFaqs, fetchArticles, search, statusFilter]);

  // === Changelog handlers ===
  const openCreateEntry = () => {
    setEditingEntryUuid(null);
    setEntryForm({ version: '', title: '', description: '', published_date: new Date().toISOString().split('T')[0], status: 'draft', audience_roles: '' });
    setEntryItems([{ type: 'feature', description: '', sort_order: 0 }]);
    setShowEntryForm(true);
  };
  const openEditEntry = async (uuid: string) => {
    const entry = await fetchChangelogEntry(uuid);
    if (!entry) return toast.error('Failed to load change log entry');
    setEditingEntryUuid(uuid);
    setEntryForm({ version: entry.version, title: entry.title, description: entry.description || '', published_date: entry.published_date ? entry.published_date.split('T')[0] : '', status: entry.status, audience_roles: entry.audience_roles || '' });
    setEntryItems(entry.items || [{ type: 'feature', description: '', sort_order: 0 }]);
    setShowEntryForm(true);
  };
  const handleSaveEntry = async () => {
    if (!entryForm.version.trim() || !entryForm.title.trim() || !entryForm.published_date) return toast.error('Version, title, and release date are required');
    const validItems = entryItems.filter((i) => i.description?.trim());
    const data = { ...entryForm, items: validItems.map((i, idx) => ({ ...i, sort_order: idx })) };
    const result = editingEntryUuid ? await updateEntry(editingEntryUuid, data) : await createEntry(data);
    if (result.success) { toast.success(result.message); setShowEntryForm(false); } else { toast.error(result.message); }
  };
  const addEntryItem = () => setEntryItems([...entryItems, { type: 'feature', description: '', sort_order: entryItems.length }]);
  const removeEntryItem = (idx: number) => setEntryItems(entryItems.filter((_, i) => i !== idx));
  const updateEntryItem = (idx: number, field: string, value: string) => { const u = [...entryItems]; (u[idx] as any)[field] = value; setEntryItems(u); };

  // === FAQ handlers ===
  const openCreateFaq = () => { setEditingFaqUuid(null); setFaqForm({ question: '', answer: '', category: 'General', sort_order: 0, is_active: true, audience_roles: '' }); setShowFaqForm(true); };
  const openEditFaq = (faq: any) => { setEditingFaqUuid(faq.uuid); setFaqForm({ question: faq.question, answer: faq.answer, category: faq.category, sort_order: faq.sort_order, is_active: !!faq.is_active, audience_roles: faq.audience_roles || '' }); setShowFaqForm(true); };
  const handleSaveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) return toast.error('Question and answer are required');
    const result = editingFaqUuid ? await updateFaqItem(editingFaqUuid, faqForm) : await createFaqItem(faqForm);
    if (result.success) { toast.success(result.message); setShowFaqForm(false); } else { toast.error(result.message); }
  };

  // === Article handlers ===
  const openCreateArticle = () => { setEditingArticleUuid(null); setArticleForm({ title: '', content: '', excerpt: '', category: 'General', tags: '', status: 'draft', audience_roles: '' }); setShowArticleForm(true); };
  const openEditArticle = async (a: any) => {
    const full = await fetchArticle(a.uuid);
    if (!full) return toast.error('Failed to load article');
    setEditingArticleUuid(full.uuid);
    setArticleForm({ title: full.title, content: full.content || '', excerpt: full.excerpt || '', category: full.category || 'General', tags: full.tags || '', status: full.status, audience_roles: full.audience_roles || '' });
    setShowArticleForm(true);
  };
  const handleSaveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.content.trim()) return toast.error('Title and content are required');
    const result = editingArticleUuid ? await updateArticleItem(editingArticleUuid, articleForm) : await createArticleItem(articleForm);
    if (result.success) { toast.success(result.message); setShowArticleForm(false); } else { toast.error(result.message); }
  };

  // === Delete ===
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const result = deleteConfirm.type === 'changelog' ? await deleteEntry(deleteConfirm.uuid) : deleteConfirm.type === 'faq' ? await deleteFaqItem(deleteConfirm.uuid) : await deleteArticleItem(deleteConfirm.uuid);
    if (result.success) { toast.success(result.message); } else { toast.error(result.message); }
    setDeleteConfirm(null);
  };

  // Group FAQs
  const faqsByCategory: Record<string, typeof faqs> = {};
  faqs.forEach((f) => { if (!faqsByCategory[f.category]) faqsByCategory[f.category] = []; faqsByCategory[f.category].push(f); });

  const createBtnLabel = activeTab === 'changelog' ? 'New Entry' : activeTab === 'faqs' ? 'New FAQ' : 'New Article';
  const createBtnAction = activeTab === 'changelog' ? openCreateEntry : activeTab === 'faqs' ? openCreateFaq : openCreateArticle;

  return (
    <div className="rn-page">
      <div className="rn-header">
        <div>
          <h1>Knowledge Base</h1>
          <p>Manage articles, change log, and frequently asked questions</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            className="rn-btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={() => window.open('/knowledge-base', '_blank', 'noopener')}
            title="Open the reader-facing Knowledge Base in a new tab"
          >
            <ExternalLink size={16} /> View Published KB
          </button>
          <button className="rn-create-btn" onClick={createBtnAction}><Plus size={18} />{createBtnLabel}</button>
        </div>
      </div>

      <div className="rn-tabs">
        <button className={`rn-tab ${activeTab === 'articles' ? 'active' : ''}`} onClick={() => { setActiveTab('articles'); setSearch(''); setStatusFilter(''); }}>
          <BookOpen size={16} /> Articles
        </button>
        <button className={`rn-tab ${activeTab === 'changelog' ? 'active' : ''}`} onClick={() => { setActiveTab('changelog'); setSearch(''); setStatusFilter(''); }}>
          <FileText size={16} /> Change Log
        </button>
        <button className={`rn-tab ${activeTab === 'faqs' ? 'active' : ''}`} onClick={() => { setActiveTab('faqs'); setSearch(''); setStatusFilter(''); }}>
          <HelpCircle size={16} /> FAQs
        </button>
      </div>

      <div className="rn-toolbar">
        <div className="rn-search">
          <Search size={16} />
          <input type="text" placeholder={activeTab === 'changelog' ? 'Search change log...' : activeTab === 'faqs' ? 'Search FAQs...' : 'Search articles...'} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {(activeTab === 'changelog' || activeTab === 'articles') && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        )}
      </div>

      {/* ARTICLES TAB */}
      {activeTab === 'articles' && (
        <div className="rn-table-container">
          {loadingArticles ? (<div className="rn-loading"><LoadingSpinner size="lg" /><p>Loading...</p></div>
          ) : articles.length === 0 ? (<div className="rn-empty"><BookOpen size={48} strokeWidth={1} /><h3>No Articles</h3><p>Create your first knowledge base article</p></div>
          ) : (
            <table className="rn-table">
              <thead><tr><th>Title</th><th>Category</th><th>Status</th><th>Updated</th><th>Actions</th></tr></thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.uuid}>
                    <td className="rn-title-cell">{a.title}{(a as any).audience_roles && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>🔒 {audienceLabels((a as any).audience_roles)}</span>}</td>
                    <td><span className="rn-category-badge">{a.category}</span></td>
                    <td><span className={`rn-status-pill ${a.status}`}>{a.status}</span></td>
                    <td>{new Date(a.updated_at).toLocaleDateString()}</td>
                    <td><div className="rn-actions">
                      <button title="Edit" onClick={() => openEditArticle(a)}><Edit2 size={14} /></button>
                      <button title="Delete" className="delete" onClick={() => setDeleteConfirm({ type: 'article', uuid: a.uuid })}><Trash2 size={14} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {activeTab === 'articles' && articlesPagination && articlesPagination.totalPages > 1 && (
        <div className="rn-pagination"><span>Page {articlesPagination.page} of {articlesPagination.totalPages}</span><div>
          <button disabled={articlesPagination.page <= 1} onClick={() => fetchArticles(articlesPagination.page - 1, search, '', statusFilter)}>Prev</button>
          <button disabled={articlesPagination.page >= articlesPagination.totalPages} onClick={() => fetchArticles(articlesPagination.page + 1, search, '', statusFilter)}>Next</button>
        </div></div>
      )}

      {/* CHANGELOG TAB */}
      {activeTab === 'changelog' && (
        <div className="rn-table-container">
          {loading ? (<div className="rn-loading"><LoadingSpinner size="lg" /><p>Loading...</p></div>
          ) : changelog.length === 0 ? (<div className="rn-empty"><FileText size={48} strokeWidth={1} /><h3>No Change Log Entries</h3><p>Create your first change log entry</p></div>
          ) : (
            <table className="rn-table">
              <thead><tr><th>Version</th><th>Title</th><th>Date</th><th>Status</th><th>Items</th><th>Actions</th></tr></thead>
              <tbody>
                {changelog.map((entry) => (
                  <tr key={entry.uuid}>
                    <td><span className="rn-version-badge">{entry.version}</span></td>
                    <td className="rn-title-cell">{entry.title}{(entry as any).audience_roles && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>🔒 {audienceLabels((entry as any).audience_roles)}</span>}</td>
                    <td>{entry.published_date ? new Date(entry.published_date).toLocaleDateString() : '—'}</td>
                    <td><span className={`rn-status-pill ${entry.status}`}>{entry.status}</span></td>
                    <td>{(entry.items || []).length}</td>
                    <td><div className="rn-actions">
                      <button title="Edit" onClick={() => openEditEntry(entry.uuid)}><Edit2 size={14} /></button>
                      {entry.status === 'draft' ? (
                        <button title="Publish" className="publish" onClick={async () => { const r = await publishEntry(entry.uuid); if (r.success) { toast.success(r.message); } else { toast.error(r.message); } }}><Send size={14} /></button>
                      ) : (
                        <button title="Unpublish" className="unpublish" onClick={async () => { const r = await unpublishEntry(entry.uuid); if (r.success) { toast.success(r.message); } else { toast.error(r.message); } }}><EyeOff size={14} /></button>
                      )}
                      <button title="Delete" className="delete" onClick={() => setDeleteConfirm({ type: 'changelog', uuid: entry.uuid })}><Trash2 size={14} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {activeTab === 'changelog' && pagination && pagination.totalPages > 1 && (
        <div className="rn-pagination"><span>Page {pagination.page} of {pagination.totalPages}</span><div>
          <button disabled={pagination.page <= 1} onClick={() => fetchChangelog(pagination.page - 1, search, statusFilter)}>Prev</button>
          <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchChangelog(pagination.page + 1, search, statusFilter)}>Next</button>
        </div></div>
      )}

      {/* FAQS TAB */}
      {activeTab === 'faqs' && (
        <div className="rn-table-container">
          {loadingFaqs ? (<div className="rn-loading"><LoadingSpinner size="lg" /><p>Loading...</p></div>
          ) : faqs.length === 0 ? (<div className="rn-empty"><HelpCircle size={48} strokeWidth={1} /><h3>No FAQs</h3><p>Create your first FAQ</p></div>
          ) : (
            <div className="rn-faq-list">
              {Object.entries(faqsByCategory).map(([cat, items]) => (
                <div key={cat} className="rn-faq-group">
                  <div className="rn-faq-group-header" onClick={() => setCollapsedCats((p) => ({ ...p, [cat]: !p[cat] }))}>
                    {collapsedCats[cat] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    <span className="rn-faq-group-name">{cat}</span>
                    <span className="rn-faq-group-count">{items.length}</span>
                  </div>
                  {!collapsedCats[cat] && items.map((faq) => (
                    <div key={faq.uuid} className="rn-faq-item">
                      <div className="rn-faq-item-content">
                        <div className="rn-faq-question">{faq.question}</div>
                        <div className="rn-faq-answer">{faq.answer.length > 120 ? faq.answer.substring(0, 120) + '...' : faq.answer}</div>
                      </div>
                      <div className="rn-faq-item-meta">
                        {(faq as any).audience_roles && <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>🔒 {audienceLabels((faq as any).audience_roles)}</span>}
                        <span className={`rn-faq-active-badge ${faq.is_active ? 'active' : 'inactive'}`}>{faq.is_active ? 'Active' : 'Inactive'}</span>
                        <div className="rn-actions">
                          <button title="Edit" onClick={() => openEditFaq(faq)}><Edit2 size={14} /></button>
                          <button title="Delete" className="delete" onClick={() => setDeleteConfirm({ type: 'faq', uuid: faq.uuid })}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CHANGELOG FORM MODAL */}
      {showEntryForm && (
        <div className="rn-modal-overlay" onClick={() => setShowEntryForm(false)}>
          <div className="rn-modal rn-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="rn-modal-header">
              <h2>{editingEntryUuid ? 'Edit Change Log Entry' : 'Create Change Log Entry'}</h2>
              <button className="rn-modal-close" onClick={() => setShowEntryForm(false)}><X size={20} /></button>
            </div>
            <div className="rn-modal-body">
              <div className="rn-form-row">
                <div className="rn-form-field"><label>Version *</label><input type="text" placeholder="e.g. v2.1.0" value={entryForm.version} onChange={(e) => setEntryForm({ ...entryForm, version: e.target.value })} /></div>
                <div className="rn-form-field"><label>Date *</label><input type="date" value={entryForm.published_date} onChange={(e) => setEntryForm({ ...entryForm, published_date: e.target.value })} /></div>
              </div>
              <div className="rn-form-field"><label>Title *</label><input type="text" placeholder="e.g. Performance Improvements & New Dashboard" value={entryForm.title} onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })} /></div>
              <div className="rn-form-field"><label>Description</label><textarea rows={3} placeholder="Brief overview..." value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })} /></div>
              <div className="rn-form-field">
                <label>Status</label>
                <select value={entryForm.status} onChange={(e) => setEntryForm({ ...entryForm, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <AudienceSelector value={entryForm.audience_roles} onChange={(v) => setEntryForm({ ...entryForm, audience_roles: v })} />
              <div className="rn-form-field">
                <label>Changes ({entryItems.filter((i) => i.description?.trim()).length} items)</label>
                <div className="rn-items-list">
                  {entryItems.map((item, idx) => (
                    <div key={idx} className="rn-item-row">
                      <select value={item.type || 'feature'} onChange={(e) => updateEntryItem(idx, 'type', e.target.value)}>
                        {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <input type="text" placeholder="Describe the change..." value={item.description || ''} onChange={(e) => updateEntryItem(idx, 'description', e.target.value)} />
                      <button className="rn-item-remove" onClick={() => removeEntryItem(idx)} title="Remove"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                <button className="rn-add-item-btn" onClick={addEntryItem}><Plus size={14} /> Add Change</button>
              </div>
            </div>
            <div className="rn-modal-footer">
              <button className="rn-btn-secondary" onClick={() => setShowEntryForm(false)}>Cancel</button>
              <button className="rn-btn-primary" onClick={handleSaveEntry} disabled={saving}>{saving ? 'Saving...' : editingEntryUuid ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ FORM MODAL */}
      {showFaqForm && (
        <div className="rn-modal-overlay" onClick={() => setShowFaqForm(false)}>
          <div className="rn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rn-modal-header">
              <h2>{editingFaqUuid ? 'Edit FAQ' : 'Create FAQ'}</h2>
              <button className="rn-modal-close" onClick={() => setShowFaqForm(false)}><X size={20} /></button>
            </div>
            <div className="rn-modal-body">
              <div className="rn-form-field"><label>Question *</label><input type="text" placeholder="e.g. How do I reset my password?" value={faqForm.question} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} /></div>
              <div className="rn-form-field"><label>Answer *</label><textarea rows={4} placeholder="Provide a detailed answer..." value={faqForm.answer} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} /></div>
              <div className="rn-form-row">
                <div className="rn-form-field"><label>Category</label><input type="text" placeholder="e.g. Account, Courses" value={faqForm.category} onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })} /></div>
                <div className="rn-form-field"><label>Sort Order</label><input type="number" value={faqForm.sort_order} onChange={(e) => setFaqForm({ ...faqForm, sort_order: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div className="rn-form-field">
                <label className="rn-checkbox-label"><input type="checkbox" checked={faqForm.is_active} onChange={(e) => setFaqForm({ ...faqForm, is_active: e.target.checked })} /> Active (visible to users)</label>
              </div>
              <AudienceSelector value={faqForm.audience_roles} onChange={(v) => setFaqForm({ ...faqForm, audience_roles: v })} />
            </div>
            <div className="rn-modal-footer">
              <button className="rn-btn-secondary" onClick={() => setShowFaqForm(false)}>Cancel</button>
              <button className="rn-btn-primary" onClick={handleSaveFaq} disabled={saving}>{saving ? 'Saving...' : editingFaqUuid ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ARTICLE FORM MODAL */}
      {showArticleForm && (
        <div className="rn-modal-overlay" onClick={() => setShowArticleForm(false)}>
          <div className="rn-modal rn-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="rn-modal-header">
              <h2>{editingArticleUuid ? 'Edit Article' : 'Create Article'}</h2>
              <button className="rn-modal-close" onClick={() => setShowArticleForm(false)}><X size={20} /></button>
            </div>
            <div className="rn-modal-body">
              <div className="rn-form-field"><label>Title *</label><input type="text" placeholder="Article title" value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} /></div>
              <div className="rn-form-field"><label>Excerpt</label><textarea rows={2} placeholder="Short summary shown in listings..." value={articleForm.excerpt} onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })} /></div>
              <div className="rn-form-field"><label>Content *</label><MarkdownEditor value={articleForm.content} onChange={(val) => setArticleForm({ ...articleForm, content: val })} placeholder="Write your article content using markdown..." rows={12} /></div>
              <div className="rn-form-row">
                <div className="rn-form-field"><label>Category</label><input type="text" placeholder="e.g. Getting Started, Features" value={articleForm.category} onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })} /></div>
                <div className="rn-form-field"><label>Tags</label><input type="text" placeholder="Comma-separated tags" value={articleForm.tags} onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })} /></div>
              </div>
              <div className="rn-form-field">
                <label>Status</label>
                <select value={articleForm.status} onChange={(e) => setArticleForm({ ...articleForm, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <AudienceSelector value={articleForm.audience_roles} onChange={(v) => setArticleForm({ ...articleForm, audience_roles: v })} />
            </div>
            <div className="rn-modal-footer">
              <button className="rn-btn-secondary" onClick={() => setShowArticleForm(false)}>Cancel</button>
              <button className="rn-btn-primary" onClick={handleSaveArticle} disabled={saving}>{saving ? 'Saving...' : editingArticleUuid ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div className="rn-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="rn-modal rn-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="rn-modal-header"><h2>Confirm Delete</h2></div>
            <div className="rn-modal-body"><p>Are you sure? This action cannot be undone.</p></div>
            <div className="rn-modal-footer">
              <button className="rn-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="rn-btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseManagement;
